import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/saint-age-requirements
 * SAINT® is FDA-cleared for adults (we use 22+ elsewhere on the site to match
 * the current SAINT® labeling). Honest, non-alarmist on teens/accelerated TMS.
 * Per the approved SAINT® facts: FDA-cleared for adults with major depressive
 * disorder who did not improve on prior antidepressant medication.
 */
const faqs: LearnFAQ[] = [
  {
    question: "How old do you have to be for SAINT?",
    answer:
      "SAINT\u00ae is FDA-cleared for adults with major depressive disorder who did not improve on prior antidepressant medication in the current episode. On our site we describe the program for ages 22 and older. A clinic that offers an accelerated course to a younger patient is using the protocol off-label \u2014 ask to see the labeling.",
  },
  {
    question: "Can teenagers get TMS?",
    answer:
      "One standard (non-accelerated) TMS device received FDA clearance as an add-on for adolescents. Accelerated, 5-day TMS in teens is off-label \u2014 it was not the protocol studied in the SAINT\u00ae trials. A child & adolescent psychiatrist should guide any decision for a minor.",
  },
  {
    question: "Is it safe for a clinic to offer 5-day TMS to a 13-year-old?",
    answer:
      "There is no FDA clearance for an accelerated 5-day TMS course at that age, and the SAINT\u00ae trials did not study adolescents. Parents should ask about the evidence, the device, and who supervises. This is not a crisis \u2014 it\u2019s a question to ask before you book. Talk to a child & adolescent psychiatrist.",
  },
  {
    question:
      "What options exist for a teen with treatment-resistant depression?",
    answer:
      "Age-appropriate, FDA-cleared options and a full evaluation with a child & adolescent psychiatrist are the right starting point. If you are in crisis, call or text 988. We do not offer SAINT\u00ae to patients under the age on the FDA labeling.",
  },
];

export const pageSaintAgeRequirements = makeArticle({
  slug: "saint-age-requirements",
  title:
    "SAINT\u00ae TMS Age Requirements: Can Teens Get SAINT\u00ae or 5-Day TMS?",
  description:
    "SAINT\u00ae is FDA-cleared for adults. Here\u2019s what that means for teens and young adults, and what to know before any clinic offers 5-day TMS to a minor.",
  canonical: "/learn/saint-age-requirements",
  h1: "Is SAINT\u00ae Approved for Teens? Age Requirements Explained",
  breadcrumbLabel: "SAINT\u00ae Age Requirements",
  shortAnswer:
    "SAINT\u00ae is FDA-cleared for adults with major depressive disorder who did not improve on prior antidepressant medication. On our site we describe the program for ages 22 and older. One standard (non-accelerated) TMS device has FDA clearance for adolescents, but an accelerated 5-day course in teens is off-label \u2014 not the protocol studied in the SAINT\u00ae trials.",
  keyTakeaways: [
    "SAINT\u00ae is FDA-cleared for adults; we describe the program for ages 22 and older.",
    "One standard (non-accelerated) TMS device has FDA clearance for adolescents.",
    "An accelerated 5-day course in teens is off-label \u2014 not studied in the SAINT\u00ae trials.",
    "If a clinic offers an accelerated program to a teen, ask: is this FDA-cleared for my child\u2019s age, or off-label?",
    "For a teen, start with a child & adolescent psychiatrist. If you\u2019re in crisis, call or text 988.",
  ],
  sections: [
    {
      heading: "How old do you have to be for SAINT?",
      paragraphs: [
        "SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation Therapy) is FDA-cleared for adults with major depressive disorder who did not improve on prior antidepressant medication in the current episode. On our site we describe the program for ages 22 and older, to match the way we triage and the current labeling we follow.",
        "The age limit exists because of who was studied: the SAINT\u00ae clinical trials enrolled adults. Results from those trials \u2014 79% remission in the controlled trial (Cole et al., Am J Psychiatry 2022) \u2014 apply to that adult population. They can\u2019t be assumed for a younger age group that wasn\u2019t studied.",
      ],
    },
    {
      heading: "Can teenagers get TMS?",
      paragraphs: [
        "Yes, with an important distinction. One standard (non-accelerated) TMS device received FDA clearance as an add-on treatment for adolescents with depression. That is a conventional daily-schedule course, not a 5-day accelerated week.",
        "An accelerated 5-day TMS course in teens is off-label \u2014 it was not the protocol tested in the SAINT\u00ae trials, and the SAINT\u00ae labeling we follow is for adults. A child & adolescent psychiatrist should guide any decision about brain-stimulation treatment for a minor.",
      ],
    },
    {
      heading: "Is it safe for a clinic to offer 5-day TMS to a 13-year-old?",
      paragraphs: [
        "There is no FDA clearance for an accelerated 5-day TMS course at that age, and the SAINT\u00ae trials did not study adolescents. This is not something to panic about \u2014 it\u2019s a question to ask before you book. Ask the clinic: what evidence supports this schedule for my child\u2019s age, what device is used, and who supervises the care?",
        "We do not offer SAINT\u00ae to patients under the age on the FDA labeling. For a child or teen, the right first step is a full evaluation with a board-certified child & adolescent psychiatrist.",
      ],
    },
    {
      heading:
        "What options exist for a teen with treatment-resistant depression?",
      paragraphs: [
        "Age-appropriate, FDA-cleared options and a thorough evaluation with a child & adolescent psychiatrist are the right starting point. If your child is in crisis, call or text 988 right away \u2014 that\u2019s the fastest way to get help.",
        "You can read more in our content for families: [[Is it a hard year, or a real illness?|/teen-depression-hard-year-or-real-illness]] and [[Diagnosed with depression as a young adult|/diagnosed-with-depression-as-a-young-adult]]. We do not offer accelerated SAINT\u00ae to patients under the age on the FDA labeling.",
      ],
    },
    {
      heading: "Young adults (22+): the SAINT\u00ae path",
      paragraphs: [
        "For adults 22 and older with major depressive disorder who haven\u2019t improved on antidepressant medication, SAINT\u00ae may be an option after a full evaluation. [[Check whether you may qualify|/learn/who-qualifies-for-saint-tms]] or call 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: [
    {
      title: "Who Qualifies for SAINT\u00ae TMS?",
      href: "/learn/who-qualifies-for-saint-tms",
      description: "Eligibility and contraindications for adults",
    },
    {
      title: "What Is SAINT\u00ae TMS?",
      href: "/learn/what-is-saint-tms",
      description: "FDA clearance, protocol, and evidence",
    },
    {
      title: "SAINT\u00ae vs Traditional TMS",
      href: "/learn/saint-vs-traditional-tms",
      description: "How SAINT\u00ae compares to standard TMS",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  safetyBlock:
    "SAINT\u00ae is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure. If you or your child are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT\u00ae is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. Neuro Wellness TMS Centers of America does not name or endorse any competitor. Individual results vary. No outcome is guaranteed. Coverage varies by plan.",
  ctaBand: {
    headline: "Questions about eligibility? Ask us, free",
    primaryText: "Check if I may be a candidate",
    primaryHref: "/am-i-a-candidate",
    secondaryText: "Talk to our team",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
